/**
 * @module components/Layout
 * @description Main layout component wrapping all pages.
 * Provides consistent structure with header, footer, cart, and checkout overlays.
 * Manages cart and checkout visibility states.
 */

import React from 'react';
import cx from 'classnames';

import { useCart } from '@/providers/CartProvider';
import Header from './Header';
import MiniCart from './MiniCart';

import { Checkout } from './Checkout';
import { Footer } from './Footer';

/**
 * @typedef LayoutProps
 * @property {string} [className] - Optional CSS className (unused)
 * @property {React.ReactNode} children - Page content to render in layout
 */

/**
 * @component Layout
 * @summary Main site layout with header, footer, cart, and checkout overlays.
 * Provides consistent page structure and manages cart/checkout state visibility.
 * @type {React.FC<LayoutProps>}
 * @param {LayoutProps} props - Component props
 * @returns {React.ReactElement} Complete page layout structure
 */
function Layout(props) {
  const { isOpen, isCheckoutOpen } = useCart();

  return (
    <>
      <div className="fix fill f jce">
        <Checkout />
        <MiniCart />
      </div>

      <div className="f jce">
        <div className="rel x main bcb f fdc">
          <Header className="x" />

          <div className="container--l mxa x p15 outer">
            <main>{props.children}</main>
          </div>

          <Footer className="x mta" />
        </div>

        <div
          className={cx('space rel', {
            'show-cart': isOpen,
            'show-checkout': isCheckoutOpen,
          })}
        />
      </div>
    </>
  );
}

export default Layout;
